import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface DealershipSettings {
  monthly_demo_target: number;
  monthly_followup_target: number;
  csi_green_target: number;
  require_next_step: boolean;
  stale_deal_hours: number;
  hot_requires_review: boolean;
  min_daily_activities: number;
  followup_sla_hours: number;
}

export const DEFAULT_SETTINGS: DealershipSettings = {
  monthly_demo_target: 0,
  monthly_followup_target: 0,
  csi_green_target: 80,
  require_next_step: true,
  stale_deal_hours: 72,
  hot_requires_review: false,
  min_daily_activities: 0,
  followup_sla_hours: 0,
};

export function useDealershipSettings() {
  const { mode, dealershipId, userId } = useAuth();
  const [settings, setSettings] = useState<DealershipSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (mode !== 'real' || !dealershipId) {
      setSettings(DEFAULT_SETTINGS);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data } = await supabase
      .from('dealership_settings')
      .select('*')
      .eq('dealership_id', dealershipId)
      .maybeSingle();
    if (data) {
      setSettings({
        monthly_demo_target: data.monthly_demo_target,
        monthly_followup_target: data.monthly_followup_target,
        csi_green_target: data.csi_green_target,
        require_next_step: data.require_next_step,
        stale_deal_hours: data.stale_deal_hours,
        hot_requires_review: data.hot_requires_review,
        min_daily_activities: data.min_daily_activities,
        followup_sla_hours: data.followup_sla_hours,
      });
    }
    setLoading(false);
  }, [mode, dealershipId]);

  useEffect(() => { load(); }, [load]);

  const save = useCallback(async (patch: Partial<DealershipSettings>) => {
    if (mode !== 'real' || !dealershipId || !userId) return { error: 'Not available in demo mode' };
    setSaving(true);
    const { error } = await supabase
      .from('dealership_settings')
      .upsert({ dealership_id: dealershipId, ...patch, updated_by: userId }, { onConflict: 'dealership_id' });
    setSaving(false);
    if (!error) setSettings(prev => ({ ...prev, ...patch }));
    return { error: error?.message ?? null };
  }, [mode, dealershipId, userId]);

  return { settings, loading, saving, save, reload: load };
}